import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { CheckIcon } from 'lucide-react'
import { inject, observer, useLocalObservable } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import SortOrder from '../../enums/SortOrder'
import Localization from '../../localization/LocalizationManager'
import Workflow from '../../models/Workflow'
import CompaniesStore from '../../stores/domain/Companies.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import MaterialsStore from '../../stores/domain/Materials.store'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import ProjectsStore from '../../stores/domain/Projects.store'
import WorkflowCategoriesStore from '../../stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import { isEventFromOwnDom } from '../../utils/isEventFromOwnDom'
import { UNASSIGNED } from '../../utils/ZoneLevelLocationConstants'
import {
  CardList,
  CardListEmpty,
  CardListSummaryBar,
  ICardListGroup,
  ICardListGroupConfig,
  ICardListState,
  useCardListCore,
} from '../CardList'
import { Checkbox } from '../shadcn-ui/checkbox'
import PermitCard from '../SitemapCards/PermitCard'
import WorkflowGroupHeader from './WorkflowGroupHeader'
import WorkflowGroupingStore from './WorkflowGroupingStore'
import { WorkflowGroupingKey, WorkflowSortingKey } from './WorkflowList.types'

import './WorkflowList.scss'

const EMPTY_STRING_ARRAY: string[] = []

interface IProps {
  workflows: Workflow[]
  loading?: boolean
  groupingKey?: WorkflowGroupingKey | null
  sortKey?: WorkflowSortingKey | null
  sortOrder?: SortOrder
  isReadOnly?: boolean
  isMultiple?: boolean
  selectedIds?: string[]
  /** The row the map is showing. Distinct from `selectedIds`, which is the bulk-action set. */
  activeWorkflowId?: string
  onSelectionChange?: (ids: string[]) => void
  onWorkflowClick?: (workflow: Workflow) => void
  onSingleSelect?: (workflow: Workflow) => void
  displayOnlyBacklinks?: boolean
  onBacklinksWorkflowClick?: (workflow: Workflow) => void
  toolbarExtra?: React.ReactNode
  cardListClassName?: string
  /** `menu`: select-menu rows, band and counter for pickers. Logistics keeps `list`. */
  variant?: 'list' | 'menu'
  workflowTypesStore?: WorkflowTypesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  materialsStore?: MaterialsStore
  projectsStore?: ProjectsStore
}

// ObservableSet gives per-key tracking on .has() for granular row re-renders.
function createSelectionStore() {
  return {
    ids: new Set<string>(),

    isSelected(id: string) {
      return this.ids.has(id)
    },

    toggle(id: string) {
      if (this.ids.has(id)) {
        this.ids.delete(id)
      } else {
        this.ids.add(id)
      }
    },

    toggleGroup(items: Workflow[]) {
      const allSelected = items.every(w => this.ids.has(w.id))
      if (allSelected) {
        items.forEach(w => this.ids.delete(w.id))
      } else {
        items.forEach(w => this.ids.add(w.id))
      }
    },

    areAllSelected(workflows: Workflow[]) {
      if (!workflows.length) return false
      return workflows.every(w => this.ids.has(w.id))
    },

    sync(newIds: string[]) {
      this.ids.clear()
      newIds.forEach(id => this.ids.add(id))
    },

    toArray(): string[] {
      return Array.from(this.ids)
    },
  }
}

type ISelectionStore = ReturnType<typeof createSelectionStore>

// Re-renders only when this workflow's selection bit flips.
const WorkflowRow = observer(
  ({
    workflow,
    selection,
    isActive,
    onWorkflowClick,
    onSelectionToggle,
    onSingleSelect,
    isReadOnly,
    isMultiple = true,
    displayOnlyBacklinks,
    onBacklinksWorkflowClick,
  }: {
    workflow: Workflow
    selection: ISelectionStore
    isActive: boolean
    onWorkflowClick?: (workflow: Workflow) => void
    onSelectionToggle: (workflow: Workflow) => void
    onSingleSelect?: (workflow: Workflow) => void
    isReadOnly: boolean
    isMultiple?: boolean
    displayOnlyBacklinks?: boolean
    onBacklinksWorkflowClick?: (workflow: Workflow) => void
  }) => {
    const isSelected = selection.isSelected(workflow.id)

    const handleRowClick =
      !isMultiple && onSingleSelect
        ? (e: React.MouseEvent) => {
            if (!isEventFromOwnDom(e)) return
            e.stopPropagation()
            onSingleSelect(workflow)
          }
        : onWorkflowClick
          ? (e: React.MouseEvent) => {
              if (!isEventFromOwnDom(e)) return
              e.stopPropagation()
              onWorkflowClick(workflow)
            }
          : undefined

    return (
      <div
        className={classList({
          'workflow-list-row': true,
          selected: isSelected && !isReadOnly,
          // A left accent, not a background: the row's SCSS already owns `background` for the
          // checkbox selection, and the two states have to stay tellable apart.
          'tw:relative tw:before:absolute tw:before:inset-y-0 tw:before:left-0 tw:before:w-0.5 tw:before:bg-primary tw:before:content-[""]':
            isActive,
          pointer: !!handleRowClick || !isMultiple,
          'tw:pl-2.5': isReadOnly || !isMultiple,
        })}
        onClick={handleRowClick}
      >
        {!isReadOnly && isMultiple && (
          <div className="row x-center y-center no-grow checkbox-col mt20">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onSelectionToggle(workflow)}
            />
          </div>
        )}
        <PermitCard
          permit={workflow}
          displayOnlyBacklinks={displayOnlyBacklinks}
          onBacklinksWorkflowClick={
            onBacklinksWorkflowClick ??
            (isMultiple ? onWorkflowClick : undefined)
          }
        />
      </div>
    )
  },
)

// Select-menu row: rounded inset, soft fills, bare checkbox (multi) or trailing check (single).
// The whole row selects; the card content is unchanged.
const WorkflowMenuRow = observer(
  ({
    workflow,
    selection,
    onWorkflowClick,
    onSelectionToggle,
    onSingleSelect,
    isReadOnly,
    isMultiple = true,
    displayOnlyBacklinks,
    onBacklinksWorkflowClick,
  }: {
    workflow: Workflow
    selection: ISelectionStore
    onWorkflowClick?: (workflow: Workflow) => void
    onSelectionToggle: (workflow: Workflow) => void
    onSingleSelect?: (workflow: Workflow) => void
    isReadOnly: boolean
    isMultiple?: boolean
    displayOnlyBacklinks?: boolean
    onBacklinksWorkflowClick?: (workflow: Workflow) => void
  }) => {
    const isSelected = !isReadOnly && selection.isSelected(workflow.id)
    const onActivate = isReadOnly
      ? onWorkflowClick
      : isMultiple
        ? onSelectionToggle
        : onSingleSelect

    return (
      <div
        // A selectable row is a listbox option; a read-only one (backlinks) opens the workflow.
        role={onActivate ? (isReadOnly ? 'button' : 'option') : undefined}
        aria-selected={onActivate && !isReadOnly ? isSelected : undefined}
        tabIndex={onActivate ? 0 : undefined}
        className={classList({
          'tw:flex tw:items-start tw:gap-2 tw:pl-1.5 tw:pr-2 tw:rounded-md tw:outline-none tw:transition-colors tw:[&_.permit-card]:flex-1 tw:[&_.permit-card]:min-w-0 tw:[&_.permit-card]:overflow-hidden tw:[&_.permit-card]:!p-0': true,
          'tw:cursor-pointer tw:hover:bg-accent-soft tw:focus-visible:bg-accent-soft tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-ring':
            !!onActivate,
          'tw:bg-accent-soft': isSelected,
        })}
        onClick={
          onActivate
            ? (e: React.MouseEvent) => {
                if (!isEventFromOwnDom(e)) return
                e.stopPropagation()
                onActivate(workflow)
              }
            : undefined
        }
        onKeyDown={
          onActivate
            ? (e: React.KeyboardEvent) => {
                // Keys on a nested control (e.g. the backlinks button) stay with it.
                if (e.target !== e.currentTarget) return
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onActivate(workflow)
                }
              }
            : undefined
        }
      >
        {!isReadOnly && isMultiple && (
          // Centred on the card's 24px type icon (card py-3 + heading pt5).
          <span className="tw:mt-[17px] tw:flex tw:h-6 tw:shrink-0 tw:items-center">
            <Checkbox
              checked={isSelected}
              displayOnly={true}
              aria-hidden={true}
              tabIndex={-1}
            />
          </span>
        )}
        <PermitCard
          permit={workflow}
          displayOnlyBacklinks={displayOnlyBacklinks}
          onBacklinksWorkflowClick={
            onBacklinksWorkflowClick ??
            (isMultiple ? onWorkflowClick : undefined)
          }
        />
        {!isReadOnly && !isMultiple && (
          <span className="tw:mt-[17px] tw:flex tw:h-6 tw:shrink-0 tw:items-center">
            <CheckIcon
              aria-hidden="true"
              className={classList({
                'tw:size-4': true,
                'tw:invisible': !isSelected,
              })}
            />
          </span>
        )}
      </div>
    )
  },
)

// Own observer so a row toggle re-renders only the counter, not the whole list.
const WorkflowMenuCounter = observer(
  ({
    workflows,
    selection,
    listState,
    isSelectable,
    onSelectAll,
    toolbarExtra,
  }: {
    workflows: Workflow[]
    selection: ISelectionStore
    listState?: ICardListState<Workflow>
    isSelectable: boolean
    onSelectAll: () => void
    toolbarExtra?: React.ReactNode
  }) => {
    const allSelected = selection.areAllSelected(workflows)
    const someSelected =
      !allSelected && workflows.some(w => selection.isSelected(w.id))
    const selectedCount = selection.ids.size

    return (
      <CardListSummaryBar
        selectAll={
          isSelectable
            ? {
                checked: allSelected,
                indeterminate: someSelected,
                disabled: workflows.length === 0,
                onToggle: onSelectAll,
                ariaLabel: Localization.translator.selectAll,
              }
            : undefined
        }
        listState={listState}
        trailing={toolbarExtra}
      >
        {isSelectable && selectedCount > 0
          ? Localization.translator.xSelected(selectedCount)
          : Localization.translator.xWorkflows(workflows.length)}
      </CardListSummaryBar>
    )
  },
)

// Group header reads the shared ObservableSet directly for independent tracking.
const GroupHeaderWrapper = observer(
  ({
    group,
    groupingKey,
    isReadOnly,
    isMultiple = true,
    onToggle,
    selection,
    onSelectionChange,
    variant,
    hasTopBorder,
  }: {
    group: ICardListGroup<Workflow>
    groupingKey: WorkflowGroupingKey | null
    isReadOnly: boolean
    isMultiple?: boolean
    onToggle: () => void
    selection: ISelectionStore
    onSelectionChange?: (ids: string[]) => void
    variant?: 'list' | 'menu'
    hasTopBorder?: boolean
  }) => {
    const allChecked = group.items.every(w => selection.ids.has(w.id))
    const someChecked =
      !allChecked && group.items.some(w => selection.ids.has(w.id))

    return (
      <WorkflowGroupHeader
        group={group}
        groupingKey={groupingKey}
        isReadOnly={isReadOnly}
        isMultiple={isMultiple}
        onToggle={onToggle}
        variant={variant}
        hasTopBorder={hasTopBorder}
        checked={allChecked}
        indeterminate={someChecked}
        onCheckboxClick={() => {
          selection.toggleGroup(group.items)
          onSelectionChange?.(selection.toArray())
        }}
      />
    )
  },
)

const WorkflowList: React.FC<IProps> = observer(
  ({
    workflows,
    loading = false,
    groupingKey = null,
    sortKey = null,
    sortOrder: sortOrderProp,
    isReadOnly = false,
    isMultiple = true,
    selectedIds = EMPTY_STRING_ARRAY,
    activeWorkflowId,
    onSelectionChange,
    onWorkflowClick,
    onSingleSelect,
    displayOnlyBacklinks,
    onBacklinksWorkflowClick,
    toolbarExtra,
    cardListClassName,
    variant = 'list',
    workflowTypesStore,
    companiesStore,
    projectMembersStore,
    locationAttributesStore,
    workflowCategoriesStore,
    materialsStore,
    projectsStore,
  }) => {
    const groupingStoreRef = useRef(
      new WorkflowGroupingStore({
        workflowTypesStore,
        workflowCategoriesStore,
        companiesStore,
        projectMembersStore,
        locationAttributesStore,
        materialsStore,
        projectsStore,
      }),
    )
    const groupingStore = groupingStoreRef.current

    const groupConfig = useMemo(():
      | ICardListGroupConfig<Workflow>
      | undefined => {
      if (!groupingKey) return undefined
      return {
        groupBy: (workflow: Workflow) =>
          groupingStore.getCategoryId(workflow, groupingKey),
        getGroupLabel: (key: string) =>
          groupingStore.getCategoryName(key, groupingKey),
        sortGroups: (a, b) => {
          if (a === UNASSIGNED) return 1
          if (b === UNASSIGNED) return -1
          const aPriority = groupingStore.getGroupSortPriority(a, groupingKey)
          const bPriority = groupingStore.getGroupSortPriority(b, groupingKey)
          if (aPriority !== bPriority) return aPriority - bPriority
          return groupingStore
            .getCategoryName(a, groupingKey)
            .localeCompare(groupingStore.getCategoryName(b, groupingKey))
        },
      }
    }, [groupingKey, groupingStore])

    const sortedWorkflows = useMemo(() => {
      if (!sortKey) return workflows
      return groupingStore.sortWorkflows(
        workflows,
        sortKey,
        sortOrderProp ?? SortOrder.DESC,
      )
    }, [workflows, sortKey, sortOrderProp, groupingStore])

    const listState = useCardListCore({
      items: sortedWorkflows,
      groupConfig,
    })

    const initialSelectedRef = useRef<Set<string> | null>(null)
    if (initialSelectedRef.current === null) {
      initialSelectedRef.current = new Set(selectedIds)
    }

    const firstSelectedId = useMemo(() => {
      const initial = initialSelectedRef.current
      if (!initial || initial.size === 0) return undefined
      for (const w of sortedWorkflows) {
        if (initial.has(w.id)) return w.id
      }
      return undefined
    }, [sortedWorkflows])

    const selection = useLocalObservable(createSelectionStore)

    useEffect(() => {
      const current = selection.toArray()
      if (
        current.length === selectedIds.length &&
        current.every(id => selectedIds.includes(id))
      ) {
        return
      }
      selection.sync(selectedIds)
    }, [selectedIds, selection])

    const handleSelectionToggle = useCallback(
      (workflow: Workflow) => {
        selection.toggle(workflow.id)
        onSelectionChange?.(selection.toArray())
      },
      [selection, onSelectionChange],
    )

    const handleSelectAll = useCallback(() => {
      selection.toggleGroup(sortedWorkflows)
      onSelectionChange?.(selection.toArray())
    }, [selection, sortedWorkflows, onSelectionChange])

    const activeListState = groupConfig ? listState : undefined

    const headersWithTopBorder = useMemo(() => {
      const groups = listState.visibleGroups ?? listState.groups
      const keys = new Set<string>()
      groups.forEach((group, index) => {
        const previous = groups[index - 1]
        if (previous && !previous.isCollapsed && previous.items.length > 0) {
          keys.add(group.key)
        }
      })
      return keys
    }, [listState.visibleGroups, listState.groups])

    if (variant === 'menu') {
      const isSelectable = !isReadOnly && isMultiple

      return (
        <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden">
          <WorkflowMenuCounter
            workflows={sortedWorkflows}
            selection={selection}
            listState={activeListState}
            isSelectable={isSelectable}
            onSelectAll={handleSelectAll}
            toolbarExtra={toolbarExtra}
          />

          <CardList
            className={cardListClassName}
            // Rows are options (single and multi); read-only rows are buttons, not options.
            role={isReadOnly ? undefined : 'listbox'}
            ariaMultiselectable={isSelectable}
            items={sortedWorkflows}
            listState={activeListState}
            initialScrollToItemId={firstSelectedId}
            renderItem={(workflow, _depth, position) => (
              <div
                className={classList({
                  'tw:px-2.5 tw:py-px': true,
                  'tw:pt-2.5': !!position?.isFirst,
                  'tw:pb-2.5': !!position?.isLast,
                })}
              >
                <WorkflowMenuRow
                  workflow={workflow}
                  selection={selection}
                  onWorkflowClick={onWorkflowClick}
                  onSelectionToggle={handleSelectionToggle}
                  onSingleSelect={onSingleSelect}
                  isReadOnly={isReadOnly}
                  isMultiple={isMultiple}
                  displayOnlyBacklinks={displayOnlyBacklinks}
                  onBacklinksWorkflowClick={onBacklinksWorkflowClick}
                />
              </div>
            )}
            renderGroupHeader={
              activeListState
                ? (group, onToggle) => (
                    <GroupHeaderWrapper
                      group={group}
                      groupingKey={groupingKey}
                      isReadOnly={isReadOnly}
                      isMultiple={isMultiple}
                      onToggle={onToggle}
                      selection={selection}
                      onSelectionChange={onSelectionChange}
                      variant="menu"
                      hasTopBorder={headersWithTopBorder.has(group.key)}
                    />
                  )
                : undefined
            }
            empty={
              !loading ? (
                <CardListEmpty
                  message={Localization.translator.noDataAvailable}
                />
              ) : undefined
            }
          />
        </div>
      )
    }

    return (
      <div className="workflow-list col overflow-hidden">
        <CardListSummaryBar
          // Logistics rows centre their checkbox in a 40px column (12px in).
          className={!isReadOnly && isMultiple ? 'tw:pl-3' : undefined}
          selectAll={
            !isReadOnly && isMultiple
              ? {
                  checked: selection.areAllSelected(sortedWorkflows),
                  onToggle: handleSelectAll,
                  ariaLabel: Localization.translator.selectAll,
                }
              : undefined
          }
          listState={activeListState}
          trailing={toolbarExtra}
        >
          {Localization.translator.xWorkflows(sortedWorkflows.length)}
        </CardListSummaryBar>

        <div className="col overflow-hidden full-height">
          <CardList
            className={cardListClassName}
            items={sortedWorkflows}
            listState={activeListState}
            initialScrollToItemId={firstSelectedId}
            renderItem={workflow => (
              <WorkflowRow
                workflow={workflow}
                selection={selection}
                isActive={workflow.id === activeWorkflowId}
                onWorkflowClick={onWorkflowClick}
                onSelectionToggle={handleSelectionToggle}
                onSingleSelect={onSingleSelect}
                isReadOnly={isReadOnly}
                isMultiple={isMultiple}
                displayOnlyBacklinks={displayOnlyBacklinks}
                onBacklinksWorkflowClick={onBacklinksWorkflowClick}
              />
            )}
            renderGroupHeader={
              activeListState
                ? (group, onToggle) => (
                    <GroupHeaderWrapper
                      group={group}
                      groupingKey={groupingKey}
                      isReadOnly={isReadOnly}
                      isMultiple={isMultiple}
                      onToggle={onToggle}
                      selection={selection}
                      onSelectionChange={onSelectionChange}
                    />
                  )
                : undefined
            }
            empty={
              !loading ? (
                <CardListEmpty
                  message={Localization.translator.noDataAvailable}
                />
              ) : undefined
            }
          />
        </div>
      </div>
    )
  },
)

export default inject(
  'workflowTypesStore',
  'companiesStore',
  'projectMembersStore',
  'locationAttributesStore',
  'workflowCategoriesStore',
  'materialsStore',
  'projectsStore',
)(WorkflowList)
